import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';

import { UserContext } from '~/components/providers/UserProvider';
import { useIsBiggerThanDesktop, useIsBiggerThanTablet } from '~/domains/common/hooks/mediaQueries';
import useProtectedGuard from '~/domains/protectionGuards/hooks/useProtectedGuard';
import { ProtectionGuardType } from '~/domains/protectionGuards/types';
import { useMarketCashBalance } from '~/domains/market/hooks/useMarketCashBalance';
import { Routes } from '~/domains/common/constants/routes';
import {
  useOrderPanelStep,
  useOrderPanelActions,
  useReviewClickedKey,
} from '~/domains/market/domains/order/state/order.store';
import { useSubmitOrder } from '~/domains/market/domains/order/hooks/useSubmitOrder';
import { useOrderPusher } from '~/domains/market/domains/order/hooks/useOrderPusher';
import { usePositionCheck } from '~/domains/market/domains/order/hooks/usePositionCheck';

import { getMarketAvailabilityState, useOrderValidation } from '../../hooks/useOrderValidation';
import { useMarketPusherWithOrderGuard } from '../../hooks/useMarketPusherWithOrderGuard';
import { useStoredMarketOrder } from '../../hooks/useStoredMarketOrder';
import { useNormalizedMarketsStore } from '../../state/normalizedMarkets.store';
import { MarketPanelBuyStep } from '../MarketPanel/components/MarketPanelBuyStep/MarketPanelBuyStep';
import { MarketPanelReviewStep } from '../MarketPanel/components/MarketPanelReviewStep/MarketPanelReviewStep';
import { MarketPanelRulesStep } from '../MarketPanel/components/MarketPanelRulesStep/MarketPanelRulesStep';
import { MarketPanelSuccessStep } from '../MarketPanel/components/MarketPanelSuccessStep/MarketPanelSuccessStep';
import { getSelectionKey } from '../../utils/selectionKey';
import { useMarketSelectionPanel } from '../../hooks/useMarketSelectionPanel';
import { useMarketLocationAllowed } from '../../hooks/useMarketLocationAllowed';
import { useMarketGroupChangeReset } from '../../hooks/useMarketGroupChangeReset';
import { useMarketSelectionStore } from '../../state/marketSelection.store';

export function DesktopSelectionPanel() {
  const router = useRouter();
  const { t } = useTranslation('market');
  const { userId } = useContext(UserContext);
  const reviewClickedKey = useReviewClickedKey();
  const isDesktop = useIsBiggerThanTablet();
  const isDesktopUp = useIsBiggerThanDesktop();
  const panelStep = useOrderPanelStep();
  const { goToReview, goToBuy, reset: resetOrder, setReviewClickedKey } = useOrderPanelActions();
  const { submit, isSubmitting, clientId } = useSubmitOrder();

  useOrderPusher({ clientId });
  useMarketGroupChangeReset();

  const {
    hasSelection,
    currentSelection,
    hasAmount,
    basePricePerShare,
    feePerContract,
    shares,
    marketData,
    clearSelection,
  } = useMarketSelectionPanel();
  const activeMarketGroupId = useNormalizedMarketsStore((state) => state.activeMarketGroupId);

  const amountNum = useMemo(() => {
    if (!hasAmount) return 0;
    const state = useMarketSelectionStore.getState();
    return parseFloat(state.amount) || 0;
  }, [hasAmount]);

  const selectedMarketIds = useMemo(
    () => (currentSelection ? [currentSelection.marketId] : []),
    [currentSelection]
  );

  useMarketPusherWithOrderGuard({
    marketIds: selectedMarketIds,
    source: 'DesktopSelectionPanel',
    enabled: isDesktop && !!currentSelection,
  });

  const { balance, formattedBalance } = useMarketCashBalance();

  const { isAllowed: isLocationAllowed, locationRestrictionModal } = useMarketLocationAllowed();

  const validation = useOrderValidation({
    amount: amountNum,
    balance,
    market: marketData.market,
    side: currentSelection?.side ?? 'yes',
    isLocationAllowed,
    marketStatus: marketData.market?.status,
    feePerContract,
  });

  const { isInsufficientFunds, isNoLiquidity, isMarketClosed, isMarketUnavailable } =
    getMarketAvailabilityState(validation.error);

  const positionCheck = usePositionCheck({
    marketId: currentSelection?.marketId ?? null,
    side: currentSelection?.side ?? null,
    enabled: isDesktopUp && !!userId && !!currentSelection && panelStep === 'buy',
  });

  const selectionKey = getSelectionKey(currentSelection);

  const reviewClicked = !!selectionKey && reviewClickedKey === selectionKey;

  const { runProtectionChecks } = useProtectedGuard({
    action: 'submit an order',
    protectionLevels: [ProtectionGuardType.GEOCOMPLY, ProtectionGuardType.CONTEST],
    allowAnnonymous: true,
    isContestLocationRestrictedOverride: !isLocationAllowed,
    locationRestrictionModal,
  });

  const isAnonymous = !userId;

  const { setStoredOrder, getStoredOrder, clearAnonOrder } = useStoredMarketOrder({
    marketGroupId: activeMarketGroupId ?? currentSelection?.marketGroupId ?? null,
  });

  useEffect(() => {
    if (!userId || !activeMarketGroupId) return;

    const storedOrder = getStoredOrder();
    if (storedOrder && storedOrder.selection.marketGroupId === activeMarketGroupId) {
      const { restoreSelection } = useMarketSelectionStore.getState();
      restoreSelection(storedOrder.selection, storedOrder.amount);
      clearAnonOrder();
    }
  }, [userId, activeMarketGroupId, getStoredOrder, clearAnonOrder]);

  const handleLoginClick = useCallback(() => {
    if (!currentSelection) return;

    const { amount } = useMarketSelectionStore.getState();
    setStoredOrder(currentSelection, amount);

    router.push(Routes.signIn({ redirectTo: router.asPath }));
  }, [currentSelection, setStoredOrder, router]);

  const handleClose = useCallback(() => {
    clearSelection();
    resetOrder();
  }, [clearSelection, resetOrder]);

  const handleReviewClick = useCallback(async () => {
    setReviewClickedKey(selectionKey);
    if (positionCheck.status === 'loading') return;
    if (positionCheck.hasOppositeSidePosition) return;

    const result = await positionCheck.checkPosition();
    if (result === 'opposite_side' || result === 'error') return;

    goToReview();
  }, [goToReview, positionCheck, selectionKey, setReviewClickedKey]);

  const handleDepositClick = useCallback(() => {
    router.push(Routes.deposit());
  }, [router]);

  const handleBack = useCallback(() => {
    goToBuy();
  }, [goToBuy]);

  const handleSubmit = useCallback(async () => {
    if (!currentSelection || !marketData.market) return;

    const isAccessible = await runProtectionChecks();
    if (!isAccessible) {
      return;
    }

    submit({
      marketId: currentSelection.marketId,
      side: currentSelection.side,
      quantity: Math.floor(shares),
      price: basePricePerShare,
    });
  }, [currentSelection, marketData.market, runProtectionChecks, submit, shares, basePricePerShare]);

  if (!isDesktop || !hasSelection || !currentSelection) {
    return null;
  }

  if (panelStep === 'success') {
    return <MarketPanelSuccessStep mode="inline" onClose={handleClose} />;
  }

  if (panelStep === 'rules') {
    return <MarketPanelRulesStep mode="inline" onBack={goToReview} />;
  }

  if (panelStep === 'review') {
    return (
      <MarketPanelReviewStep
        mode="inline"
        balance={balance}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
        onBack={handleBack}
        isMarketUnavailable={isMarketUnavailable}
        isInsufficientFunds={isInsufficientFunds}
        onDepositClick={handleDepositClick}
        isAnonymous={isAnonymous}
        onLoginClick={handleLoginClick}
      />
    );
  }

  return (
    <MarketPanelBuyStep
      mode="inline"
      formattedBalance={formattedBalance}
      isInputDisabled={positionCheck.hasOppositeSidePosition}
      isActionDisabled={
        !hasSelection ||
        !hasAmount ||
        (!validation.isValid && !(isAnonymous && isInsufficientFunds))
      }
      onReviewClick={handleReviewClick}
      isReviewLoading={reviewClicked && positionCheck.status === 'loading'}
      isInsufficientFunds={isInsufficientFunds}
      onDepositClick={handleDepositClick}
      isLocationRestricted={!isLocationAllowed}
      validationErrorMessage={isAnonymous ? null : validation.errorMessage}
      positionCheckErrorMessage={
        reviewClicked && positionCheck.status === 'error' ? t('errors.positionCheckFailed') : null
      }
      isNoLiquidity={isNoLiquidity}
      isMarketClosed={isMarketClosed}
      hasOppositeSidePosition={positionCheck.hasOppositeSidePosition}
      isAnonymous={isAnonymous}
      onClose={handleClose}
      onLoginClick={handleLoginClick}
    />
  );
}
